/** @jsx jsx */

import PropTypes from 'prop-types';
import _ from 'lodash';

import { Colors } from '@zapier/theme';
import { jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import RequestAddUpdates from 'app/zapbook/components/RequestAddUpdates';
import Tag from 'app/common/components/Tag';
import purify from 'app/common/components/purify';
import { SMALL_SCREEN } from 'app/zapbook/utils/constants';

const Styles = {
  appCategories: {
    // overflow: auto is added by zapbook-refresh on mount
    overflowY: 'hidden !important',
    width: '26%',
    [`@media (max-width: ${SMALL_SCREEN})`]: {
      display: 'none',
    },
  },
  categoryTitle: {
    marginBottom: '1em',
  },
  categoryItem: {
    margin: '0.4em 0',
  },
  category: isActive => ({
    display: 'inline-block',
    verticalAlign: 'middle',
    color: isActive ? Colors.heroine : Colors.steel,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'all 0.1s ease',
    '&:hover': {
      textDecoration: 'none',
      ...(!isActive && {
        color: Colors.batmobile,
      }),
    },
  }),
  filterCategories: asSubsection =>
    asSubsection && { overflowY: 'auto', height: '30vh' },
  sortCategories: {
    minHeight: '25vh',
    overflowY: 'hidden',
  },
  inlineTag: {
    verticalAlign: 'middle',
    marginLeft: '0.5em',
  },
};

const CategoryItem = purify('CategoryItem', props => {
  return (
    <li css={Styles.categoryItem}>
      <Link
        css={Styles.category(props.isActive)}
        onClick={props.onSortOrFilter}
      >
        {props.categoryName}
      </Link>
      {props.categoryKey === 'premium' ? (
        <Tag css={Styles.inlineTag} kind="premium small">
          Premium
        </Tag>
      ) : props.categoryKey === 'upcoming' ? (
        <Tag css={Styles.inlineTag} kind="upcoming small">
          Upcoming
        </Tag>
      ) : props.categoryKey === 'early-access' ? (
        <Tag css={Styles.inlineTag} kind="early-access small">
          Early Access
        </Tag>
      ) : null}
    </li>
  );
});

CategoryItem.propTypes = {
  categoryKey: PropTypes.string.isRequired,
  activeCategories: PropTypes.arrayOf(PropTypes.string.isRequired),
  isActive: PropTypes.bool.isRequired,
  dataset: PropTypes.object.isRequired,
  categoryName: PropTypes.string.isRequired,
  onSortOrFilter: PropTypes.func.isRequired,
  categoryType: PropTypes.string.isRequired,
};

const CategorySection = purify('CategorySection', props => {
  return (
    <ul>
      {_.map(props.categories, category => {
        const categoryKey = _.values(category.dataset)[0];
        const activeCategories = [props.sortBy, props.filterBy];
        const isActive = activeCategories.indexOf(categoryKey) > -1;
        const onSortOrFilter = e => {
          e.preventDefault();
          props.onSortOrFilter(props.categoryType, categoryKey);
        };
        return (
          <CategoryItem
            activeCategories={activeCategories}
            categoryKey={categoryKey}
            categoryName={category.category}
            categoryType={props.categoryType}
            dataset={category.dataset}
            isActive={isActive}
            key={categoryKey}
            onSortOrFilter={onSortOrFilter}
          />
        );
      })}
    </ul>
  );
});

CategorySection.propTypes = {
  sortBy: PropTypes.string.isRequired,
  filterBy: PropTypes.string.isRequired,
  categories: PropTypes.array.isRequired,
  onSortOrFilter: PropTypes.func.isRequired,
  categoryType: PropTypes.string.isRequired,
};

const Categories = props => {
  return (
    <div className="app-filters" css={Styles.appCategories}>
      <h3 css={Styles.categoryTitle}>Sort Apps By</h3>
      <div css={Styles.sortCategories}>
        <CategorySection
          categories={props.sortCategories}
          categoryType={props.categoryTypes.sort}
          filterBy={props.filterBy}
          onSortOrFilter={props.onSortOrFilter}
          sortBy={props.sortBy}
        />
      </div>
      <span>
        <h3 css={Styles.categoryTitle}>Filter By Category</h3>
        <div
          css={Styles.filterCategories(
            !props.isZapbookPage && !props.isCategoryPage
          )}
        >
          <CategorySection
            categories={props.filterCategories}
            categoryType={props.categoryTypes.filter}
            filterBy={props.filterBy}
            onSortOrFilter={props.onSortOrFilter}
            sortBy={props.sortBy}
          />
        </div>
      </span>
      {props.isZapbookPage || props.isCategoryPage ? (
        <RequestAddUpdates {...props.requestAddUpdates} />
      ) : null}
    </div>
  );
};

export default Categories;
